import React, {
  HTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import Link from 'next/link'

import Bullets from '@/components/base/bullets'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Media, { MediaElement } from '@/components/base/media'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'

import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type InfographicCarouselProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    items: {
      media: MediaElement
      title: string
      paragraph: string
      link: WPLink
    }[]
  }>

const InfographicCarousel = ({ items }: InfographicCarouselProps) => {
  const emblaContainer = useRef<HTMLDivElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    breakpoints: {
      '(min-width: 768px)': { active: false }
    }
  })

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi, setSelectedIndex])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()

    emblaApi.on('select', onSelect).on('reInit', onSelect)
  }, [emblaApi, onSelect])

  return (
    <section className="overflow-hidden">
      <Container>
        <Grid>
          <div className="col-span-2 md:col-span-6 lg:col-span-12">
            <div ref={emblaRef}>
              <div
                ref={emblaContainer}
                className={cn(
                  'flex touch-pan-y gap-x-spacing-sm',
                  'md:grid md:auto-rows-fr md:grid-cols-12 md:gap-spacing-xs'
                )}
              >
                {items?.map((item, i) => {
                  return (
                    <div
                      key={i}
                      className={cn(
                        'group relative flex shrink-0 grow-0 basis-full flex-col gap-spacing-xs',
                        'md:col-span-12 md:mt-spacing-lg lg:col-span-4',
                        item?.link?.url && '[&_*]:cursor-pointer'
                      )}
                    >
                      {item?.link?.url ? (
                        <>
                          <span
                            data-background
                            className="absolute inset-0 z-0 transition-colors duration-500 ease-cubic-default group-hover:bg-surface-secondary-invert-light/20"
                          />
                          <Link
                            href={item.link.url}
                            target={item.link.target}
                            className="absolute inset-0 z-20"
                            aria-label={item?.link?.title || item?.title}
                            scroll={false}
                          >
                            {item?.link?.title ? (
                              <span className="sr-only">
                                {item.link.title || item.title}
                              </span>
                            ) : null}
                          </Link>
                        </>
                      ) : null}
                      <div className="relative z-10 flex aspect-[394/500]">
                        <Media
                          {...item?.media}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      {item.title ? (
                        <Typography variant="h6" className="relative z-10">
                          {item.title}
                        </Typography>
                      ) : null}
                      {item.paragraph ? (
                        <Typography
                          variant="regular"
                          paragraph
                          className="relative z-10"
                        >
                          {item.paragraph}
                        </Typography>
                      ) : null}
                      {item?.link?.url ? (
                        <div className="relative z-10 mt-spacing-sm flex">
                          <div className={cn('-m-[1rem] p-[1rem]')}>
                            {item?.link?.title ? (
                              <span className="sr-only">{item.link.title}</span>
                            ) : null}
                            <SVGIcon
                              symbol="next-arrow-icon"
                              className="h-[.8rem] w-[5.7rem] fill-surface-primary-light transition-transform duration-700 ease-cubic-default group-hover:translate-x-1"
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </div>
            <Bullets
              items={items?.length}
              selectedIndex={selectedIndex}
              className="mt-spacing-sm flex md:hidden"
            />
          </div>
        </Grid>
      </Container>
    </section>
  )
}

export default InfographicCarousel
